import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const LocationCapture = ({ location, setLocation }) => {
  const [isCapturing, setIsCapturing] = useState(false);
  const [error, setError] = useState(null);

  const captureLocation = () => {
    setIsCapturing(true);
    setError(null);

    if (!navigator?.geolocation) {
      setError('Geolocation is not supported by your browser');
      setIsCapturing(false);
      return;
    }

    navigator.geolocation?.getCurrentPosition(
      async (position) => {
        const latitude = position?.coords?.latitude;
        const longitude = position?.coords?.longitude;

        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
          );
          const data = await response?.json();

          setLocation({
            latitude,
            longitude,
            address: data?.display_name || `${latitude?.toFixed(6)}, ${longitude?.toFixed(6)}`,
            accuracy: position?.coords?.accuracy
          });
        } catch (err) {
          setLocation({
            latitude,
            longitude,
            address: `${latitude?.toFixed(6)}, ${longitude?.toFixed(6)}`,
            accuracy: position?.coords?.accuracy
          });
        }

        setIsCapturing(false);
      },
      (err) => {
        console.error('Geolocation error:', err);
        setError(err?.message || 'Unable to retrieve your location');
        setIsCapturing(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const clearLocation = () => {
    setLocation(null);
    setError(null);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1">
      <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center">
        <Icon name="MapPin" size={20} className="mr-2" />
        GPS Location (Optional)
      </h2>

      <div className="space-y-4">
        {!location && (
          <>
            <p className="text-sm text-muted-foreground">
              Capture your current location to help dispatch find you quickly
            </p>
            <Button
              type="button"
              variant="outline"
              size="lg"
              iconName="Navigation"
              iconPosition="left"
              onClick={captureLocation}
              loading={isCapturing}
              disabled={isCapturing}
              className="w-full"
            >
              {isCapturing ? 'Capturing Location...' : 'Capture Current Location'}
            </Button>
          </>
        )}

        {location && (
          <div className="bg-success/10 border border-success/20 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3 flex-1">
                <Icon name="MapPin" size={20} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground mb-1">Location Captured</p>
                  <p className="text-xs text-muted-foreground mb-2">{location?.address}</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-muted-foreground">Latitude:</span>
                      <span className="ml-1 font-mono text-foreground">{location?.latitude?.toFixed(6)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Longitude:</span>
                      <span className="ml-1 font-mono text-foreground">{location?.longitude?.toFixed(6)}</span>
                    </div>
                  </div>
                  {location?.accuracy && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Accuracy: ±{Math.round(location?.accuracy)}m
                    </p>
                  )}
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                iconName="X"
                onClick={clearLocation}
              />
            </div>
          </div>
        )}

        {error && (
          <div className="bg-error/10 border border-error/20 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="AlertCircle" size={20} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-error">Location Error</p>
                <p className="text-xs text-error/80 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
          <div className="flex items-start space-x-2">
            <Icon name="Info" size={16} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground">
              Your location helps dispatch send help to the right place. This is especially useful for roadside emergencies.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationCapture;